<template>
  <div class="profile-page">
    <title-bar :title="$lang.profile_title" />

    <div v-if="readError" class="profile-read-error">
      {{ readError }}
    </div>
    <div v-if="!readError && emptyState" class="profile-empty-state">
      {{ $lang.profile_empty_state || 'No profile data available yet.' }}
    </div>

    <div class="profile-list">
      <div
        v-for="(item, index) in menuItems"
        :key="index"
        class="profile-list__item"
        @click="gotoDetail(index)"
      >
        <span class="profile-list__label">{{ item.label }}</span>
        <div class="profile-list__right">
          <span v-if="item.value" class="profile-list__value">{{ item.value }}</span>
          <van-icon name="arrow" size="16" color="#757780" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Init } from '@/api/common'
import { contentOf, ensureLogin as ensureMineLogin, failMessage, handleNeedLogin, isOk } from './safeFlow'
import { getMineActionRoute } from '@/views/layout/mineNavigation'

function profileFailureMessage(error, fallback) {
  const message = failMessage(error, fallback)
  return /^network error$/i.test(String(message || '').trim()) ? fallback : message
}

function fallbackToken(value) {
  return String(value == null ? '' : value).trim().toLowerCase()
}

function isReadOk(res) {
  return isOk(res)
}

function firstProfileValue(source, keys) {
  if (!source || typeof source !== 'object') return ''
  for (const key of keys) {
    const value = source[key]
    if (value !== undefined && value !== null && value !== '') return value
  }
  return ''
}

function firstProfileArray(source, keys) {
  if (!source || typeof source !== 'object') return []
  for (const key of keys) {
    if (Array.isArray(source[key])) return source[key]
  }
  return []
}

function firstRecordObject(source) {
  if (Array.isArray(source)) return source.find(item => item && typeof item === 'object') || {}
  if (!source || typeof source !== 'object') return {}
  for (const key of ['records', 'list', 'dataList', 'items', 'pageList']) {
    const records = source[key]
    if (Array.isArray(records)) {
      const record = records.find(item => item && typeof item === 'object')
      if (record) return record
    }
  }
  return {}
}

function yesValue(value) {
  const token = fallbackToken(value)
  return value === true || value === 1 || ['true', '1', 'yes', 'y', 'on', 'set', 'setted'].includes(token)
}

function normalizeProfileContent(content) {
  const source = { ...content, ...firstRecordObject(content) }
  const bankCardList = firstProfileArray(source, ['bankCardList', 'bank_card_list', 'cardList', 'card_list', 'cards', 'bankCards', 'bank_cards'])
  const countValue = firstProfileValue(source, ['bankCardCount', 'bank_card_count', 'cardCount'])
  const count = Number(countValue)
  const bankCardCount = countValue !== '' && Number.isFinite(count) ? count : bankCardList.length
  const hasBankCardValue = firstProfileValue(source, ['hasBankCard', 'has_bank_card'])
  return {
    account: firstProfileValue(source, ['account', 'userName', 'user_name', 'username', 'loginName', 'login_name']) || '',
    realName: firstProfileValue(source, ['realName', 'real_name', 'name']) || '',
    headUrl: firstProfileValue(source, ['headUrl', 'head_url', 'avatar']) || '',
    privacyPasswdSetted: yesValue(firstProfileValue(source, ['privacyPasswdSetted', 'privacy_passwd_setted', 'hasWithdrawPassword'])) ? 'yes' : 'no',
    bankCardList,
    bankCardCount,
    hasBankCard: yesValue(hasBankCardValue) || bankCardCount > 0 || bankCardList.length > 0
  }
}

function isEmptyProfile(profile) {
  return !profile.account &&
    !profile.realName &&
    !profile.headUrl &&
    profile.privacyPasswdSetted !== 'yes' &&
    profile.bankCardCount <= 0 &&
    profile.bankCardList.length === 0 &&
    !profile.hasBankCard
}

export default {
  name: 'Profile',
  data() {
    return {
      realName: '',
      headUrl: '',
      account: '',
      privacyPasswdSetted: 'no',
      bankCardList: [],
      bankCardCount: 0,
      hasBankCard: false,
      readError: '',
      emptyState: false
    }
  },
  computed: {
    bankCardValue() {
      return this.bankCardCount > 0 ? String(this.bankCardCount) : ''
    },
    profileContext() {
      return {
        realName: this.realName,
        privacyPasswdSetted: this.privacyPasswdSetted,
        bankCardList: this.bankCardList,
        bankCardCount: this.bankCardCount,
        hasBankCard: this.hasBankCard
      }
    },
    menuItems() {
      return [
        { label: this.$lang.profile_account_name, value: this.account },
        { label: this.$lang.profile_real_name, value: this.realName },
        { label: this.$lang.profile_login_pass, value: '' },
        { label: this.$lang.profile_withdraw_pass, value: '' },
        { label: this.$lang.profile_bank_card, value: this.bankCardValue },
        { label: this.$lang.profile_avatar, value: '' },
        { label: this.$lang.profile_privacy, value: '' }
      ]
    }
  },
  mounted() {
    this.loadInit()
  },
  methods: {
    ensureLogin() {
      return ensureMineLogin(this)
    },
    resetProfileReadState() {
      this.realName = ''
      this.headUrl = ''
      this.account = ''
      this.privacyPasswdSetted = 'no'
      this.bankCardList = []
      this.bankCardCount = 0
      this.hasBankCard = false
      this.emptyState = true
    },
    async loadInit() {
      if (!this.ensureLogin()) return
      try {
        const data = await Init()
        if (handleNeedLogin(this, data, this.$lang.login_txt11 || this.$lang.Entrar || 'Login Now', { isValue: 'isLogin' }, { replace: true })) {
          this.readError = failMessage(data, this.$lang.service_unavailable || 'Service temporarily unavailable. Please try again later.')
          return
        }
        if (isReadOk(data)) {
          const profile = normalizeProfileContent(contentOf(data))
          this.readError = ''
          this.emptyState = isEmptyProfile(profile)
          this.account = profile.account
          this.realName = profile.realName
          this.headUrl = profile.headUrl
          this.privacyPasswdSetted = profile.privacyPasswdSetted
          this.bankCardList = profile.bankCardList
          this.bankCardCount = profile.bankCardCount
          this.hasBankCard = profile.hasBankCard
        } else {
          const message = failMessage(data, this.$lang.profile_read_failed || 'Profile data could not be loaded. Existing data has been kept.')
          this.readError = message
          this.$toast({ message, icon: 'cross' })
        }
      } catch (e) {
        const message = profileFailureMessage(e, this.$lang.profile_read_failed || 'Profile data could not be loaded. Existing data has been kept.')
        this.readError = message
        this.$toast({ message, icon: 'cross' })
      }
    },
    gotoDetail(i) {
      if (!this.ensureLogin()) return
      if (i === 0) {
        this.$jumpTo('/editProfile')
      } else if (i === 1 && !this.realName) {
        this.$jumpTo('/authName', { from: 'profile' })
      } else if (i === 2) {
        this.$jumpTo('/passwordLogin')
      } else if (i === 3) {
        this.$jumpTo('/setPassWord', { from: 'profile' })
      } else if (i === 4) {
        const route = getMineActionRoute('bank', {
          loggedIn: true,
          source: 'profile',
          profile: this.profileContext
        })
        this.$jumpTo(route.path, route.query || {})
      } else if (i === 5) {
        this.$jumpTo('/avatar', { headUrl: this.headUrl })
      } else if (i === 6) {
        this.$jumpTo('/privacy')
      }
    }
  }
}
</script>

<style lang="less" scoped>
.profile-page {
  min-height: 100vh;
  background: @cont-bg;
}

.profile-list {
  padding: 12px 16px;

  &__item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: @cont-bg;
    border-radius: 12px;
    padding: 14px 12px;
    margin-bottom: 12px;
    font-size: var(--vk-fs-base);
    color: @font-color;
    font-weight: bold;
  }

  &__right {
    display: flex;
    align-items: center;
    gap: 6px;
  }

  &__value {
    font-size: var(--vk-fs-sm);
    color: var(--vk-t2);
    font-weight: normal;
  }
}

.profile-read-error {
  margin: 12px 16px 0;
  padding: 10px 12px;
  border-radius: 8px;
  background: rgba(220, 38, 38, 0.12);
  border: 1px solid rgba(220, 38, 38, 0.28);
  color: var(--vk-danger);
  font-size: var(--vk-fs-xs);
  line-height: 18px;
}

.profile-empty-state {
  margin: 12px 16px 0;
  padding: 10px 12px;
  border-radius: 8px;
  background: rgba(148, 163, 184, 0.12);
  border: 1px solid rgba(148, 163, 184, 0.24);
  color: var(--vk-t2);
  font-size: var(--vk-fs-xs);
  line-height: 18px;
}
</style>
